import apiClient from '@/utils/api/client'
import {
  errorHandle,
  getErrorResponse,
  getSuccessResponse,
  validateResponse,
} from '../utils'
import {
  DEFAULT_ERROR_DISPLAY_INFO,
} from '../constants'
import { UnifiedApiStatus, WbffResponse } from '../types'
import * as Sentry from "@sentry/browser"
import { getConfigBaseUrl } from '@/utils/env'
import { makeCourseList } from '../asset'
import { getTranslation } from '../i18n'
import { GetInitialDataResponsePayload } from './type'
import { loggerFatal } from '@/utils/user'

export const getInitialDataWbff = async (): Promise<WbffResponse<any>> => {
  try {
    const getUserStatusPromise = apiClient.get(`/v1/user/status`)
    const getUserFixedStatusPromise = localStorage.walletFirst * 1 !== 1 ? apiClient.get(`/v1/user/fixedstatus`) : Promise.resolve(null)
    const getBrandsListPromise = apiClient.get(`/v1/brands/list`)
    const getUserAssetAllPromise = apiClient.get(`/v1/user/assets/all`)
    const getUserNewsListPromise = apiClient.get(`/v1/user/news/list?LIST_FLAG=1&PAGE_DATA_CNT=1&SEQ_NO=null&OPEN_DT=null`)
    const getUserAutosweepPromise = apiClient.get(`/v1/user/autosweep`)
    const getUserTopNoticeListPromise = apiClient.get(`/v1/user/top/notice/list`)
    const getUserBannersPromise = apiClient.get(`/v1/user/banner/list`)
    const getGlobalConfigPromise = (async () => {
      try {
        const response = await fetch(`${getConfigBaseUrl()}point-global-config.json`, {
          cache: 'no-cache',
        })
        const data = await response.json()
        return data
      } catch (e) {
        // no error handling needed for now.
        return null
      }
    })()
  
    const getUserAssetsReturnsPromise = Promise.all([getUserStatusPromise, getGlobalConfigPromise]).then((responses) => {
      const getUserStatusApiStatus = validateResponse(responses[0])

      if (getUserStatusApiStatus !== UnifiedApiStatus.Success) {
        return {
          hasError: true,
          apiStatus: getUserStatusApiStatus,
          response: responses[0]
        }
      }
      const userStatusData = responses[0].data
      const globalConfig = responses[1]
      const returnValues = {
        hasError: false,
        userGroup: userStatusData.USER_GROUP,
        attentionFlag: userStatusData.ATTENTION_FLG,
        possessBrandFlag: userStatusData.POSSESS_BRAND_FLG,
        campaignAppliedFlag: userStatusData.CAMPAIGN_APPLIED_FLG,
        miniAppAssetManagementOpenFlag: userStatusData.MINIAPP_ASSET_MANAGEMENT_OPEN_FLG,
        csrfToken: userStatusData.CSRF_TOKEN,
        accountType: userStatusData.ACCOUNT_TYPE,
        defaultGraphFlag: userStatusData.DEFAULT_GRAPH_FLG,
        abFlag: userStatusData.AB_FLG,
        clientId: userStatusData.CLIENT_ID,
        availablePoint: userStatusData.REAL_POINT,
        globalConfig,
        returns: [],
      }

      // If possess courses, ab test is enabled, and specified user group exists in global config.
      //  This covers the control group case as well.
      const needReturns =
        userStatusData.POSSESS_BRAND_FLG === 1 &&
        userStatusData.USER_GROUP &&
        globalConfig &&
        globalConfig.abTest &&
        globalConfig.abTest.enabled &&
        globalConfig.abTest.userGroups &&
        globalConfig.abTest.userGroups[userStatusData.USER_GROUP]

      // If abTest is not enabled, set USER_GROUP to null for GA tag purposes.
      if (!globalConfig || !globalConfig.abTest || !globalConfig.abTest.enabled) {
        returnValues.userGroup = null
        return returnValues
      }

      if (needReturns) {
        return new Promise<any>((resolve) => {
          // Always resolve. Not critical if error happens with this API.
          apiClient.get('/v1/user/assets/returns')
            .then((returnsRes) => {
              const returns = returnsRes ? returnsRes?.data?.RETURN_LIST : null
              returnValues.returns = returns
              resolve(returnValues)
            })
            .catch(() => resolve(returnValues))
        })
      }
      return returnValues
    })

    const [brandList, userAssetAll, userNewsList, userAutosweep, userAssetsReturns, userTopNoticeList, userFixedStatus, userBanners] = await Promise.all([
      getBrandsListPromise,
      getUserAssetAllPromise,
      getUserNewsListPromise,
      getUserAutosweepPromise,
      getUserAssetsReturnsPromise,
      getUserTopNoticeListPromise,
      getUserFixedStatusPromise,
      getUserBannersPromise,
    ])
    
    const responses = [brandList, userAssetAll, userNewsList, userAutosweep, userTopNoticeList, userFixedStatus, userBanners]

    const responseData = []
    for (let i = 0; i < responses.length; i++) {
      if (responses[i] === null) continue
      const apiStatus = validateResponse(responses[i])
      if (apiStatus === UnifiedApiStatus.Success) {
        const { data } = responses[i]
        responseData.push(data)
      } else {
        return errorHandle(apiStatus, responses[i])
      }
    }
    if (userAssetsReturns.hasError) {
      return errorHandle(userAssetsReturns.apiStatus, userAssetsReturns.response)
    }

    const {
      hasError, globalConfig, returns, ...userStatusData
    } = userAssetsReturns

    const seBrandArray = brandList.data.SE_BRAND_ARRAY.sort((a, b) => (a.SORT > b.SORT ? 1 : -1))
    const courseTranslations = await getTranslation('course')

    const courseListWithProfitLoss = makeCourseList(
      userAssetAll.data.PROFIT_LOSS_INFO_BRAND_LIST,
      courseTranslations,
      seBrandArray,
    )

    const payload: GetInitialDataResponsePayload = {
      providedToThirdPartiesFlag: userFixedStatus?.data?.PROVIDED_TO_THIRD_PARTIES_FLG,
      seBrandArray,
      userAssetAll: userAssetAll.data,
      courseListWithProfitLoss,
      hasUnread: !!userNewsList.data.HAS_UNREAD,
      autosweepAllCourseFlag: userAutosweep.data.AUTOSWEEP_ALL_COURSE_FLG,
      autosweepCourseId: userAutosweep.data.BRAND_ID,
      topNoticeArray: userTopNoticeList.data.TOP_NOTICE_ARRAY,
      topBanners: userBanners.data.TOP_BANNER_INFO_ARRAY,
      otherBanner: userBanners.data.OTHER_BANNER_INFO,
      globalConfig,
      returns,
      ...userStatusData,
    }

    return getSuccessResponse(payload)
  } catch (error) {
    Sentry.captureException(new Error('Exception happened at getInitialDataWbff'))
    return getErrorResponse(DEFAULT_ERROR_DISPLAY_INFO)
  }
}

export const getLatestAssetsWbff = async () => {
  try {
    const getUserAssetAll = await apiClient.get(`/v1/user/assets/all`)
    const getUserAssetAllApiStatus = validateResponse(getUserAssetAll)

    if (getUserAssetAllApiStatus === UnifiedApiStatus.Success) {
      const { data } = getUserAssetAll
      const courseTranslations = await getTranslation('course')
      const courseListWithProfitLoss = makeCourseList(
        data.PROFIT_LOSS_INFO_BRAND_LIST,
        courseTranslations,
      )

      const payload = {
        courseListWithProfitLoss,
        userAssetAll: data,
      }

      return getSuccessResponse(payload) 
    }

    return errorHandle(getUserAssetAllApiStatus, getUserAssetAll)
  } catch (error) {
    Sentry.captureException(new Error('Exception happened at getLatestAssetsWbff'))
    loggerFatal(
      ` Error in getLatestAssetsWbff:`,
      error instanceof Error ? error.message : JSON.stringify(error),
    )
    return getErrorResponse(DEFAULT_ERROR_DISPLAY_INFO) 
  }
}
